import { Badge, Banner, Button, Checkbox, Field, Input, InputArea, Select, Switch } from '@cloudflare/kumo';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DangerButton } from '../components/ui/DangerButton';
import { ICONS } from '../components/ui/icons';
import { useTenant } from '../contexts/TenantContext';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useAppToast } from '../hooks/useAppToast';
import { useCategories } from '../hooks/useCategories';
import { useMessageVariables } from '../hooks/useMessageVariables';
import { DangerConfirmModal } from '../components/DangerConfirmModal';
import { BrandLogo } from '../components/BrandLogo';
import { providerBrand } from '../lib/brandLogos';
import { SkeletonText } from '../components/ui/Skeleton';

type MessageType = 'text' | 'image' | 'video' | 'audio' | 'document' | 'template' | 'flow';
interface Step {
  id?: string; delayValue: number; delayUnit: 'minutes' | 'hours' | 'days';
  windowStart: string | null; windowEnd: string | null; windowDays: number[];
  messageType: MessageType; content: Record<string, any>;
  sent?: number; failed?: number; waiting?: number;
}
interface Sequence {
  id: string; name: string; active: boolean; connectionNames: string[]; stopOnReply: boolean; autoCategoryIds: string[];
  steps: Step[]; enrollments: Partial<Record<string, number>>;
}
interface Enrollment { id: string; status: string; currentStep: number; nextRunAt: string | null; lastError: string | null; stopReason: string | null; source: string; enrolledAt: string; contact: { nome: string; telefone: string } }

const DAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const UNIT_LABEL = { minutes: 'minutos', hours: 'horas', days: 'dias' };
const TYPE_LABEL: Record<MessageType, string> = { text: 'Texto', image: 'Imagem', video: 'Vídeo', audio: 'Áudio', document: 'Arquivo', template: 'Template (API oficial)', flow: 'Formulário (API oficial)' };
const STATUS_LABEL: Record<string, string> = { ACTIVE: 'Em andamento', COMPLETED: 'Concluiu', CANCELED: 'Saiu', FAILED: 'Falhou' };
const STOP_LABEL: Record<string, string> = { OPTED_OUT: 'pediu descadastro', REPLIED: 'respondeu', MANUAL: 'removido' };
const newStep = (first: boolean): Step => ({
  delayValue: first ? 0 : 1, delayUnit: first ? 'minutes' : 'days', windowStart: '09:00', windowEnd: '19:00', windowDays: [1, 2, 3, 4, 5, 6],
  messageType: 'text', content: { text: '' },
});
const input = { padding: '6px 10px', border: '1px solid var(--text-faint)', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' } as const;

/** Campanhas › Sequências › editor */
export function SequenceEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useTenant();
  const canEdit = can('campaigns:write');
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const variables = useMessageVariables();
  const { categories } = useCategories({ pageSize: 200 });
  const [seq, setSeq] = useState<Sequence | null>(null);
  const [steps, setSteps] = useState<Step[]>([]);
  const [sessions, setSessions] = useState<Array<{ name: string; displayName?: string | null; mePushName?: string | null; provider: string }>>([]);
  const [segments, setSegments] = useState<Array<{ id: string; name: string; contactCount: number }>>([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [enrollSource, setEnrollSource] = useState('');
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [enrollFilter, setEnrollFilter] = useState('ACTIVE');
  const [deleting, setDeleting] = useState(false);
  const [metaFlows, setMetaFlows] = useState<Array<{ id: string; name: string; status: string; sessionName: string }>>([]);

  const load = useCallback(async () => {
    const res = await fetchApi(`/api/sequences/${id}`);
    if (!res.ok) { toast.error('Sequência não encontrada'); navigate('/sequencias'); return; }
    const s = (await res.json()).sequence as Sequence;
    setSeq(s);
    setSteps(s.steps);
    setDirty(false);
  }, [fetchApi, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadEnrollments = useCallback(async () => {
    const res = await fetchApi(`/api/sequences/${id}/enrollments?status=${enrollFilter}`);
    if (res.ok) setEnrollments((await res.json()).enrollments);
  }, [fetchApi, id, enrollFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadEnrollments(); }, [loadEnrollments]);
  useEffect(() => {
    fetchApi('/api/connections/whatsapp-sessions').then((r) => (r.ok ? r.json() : [])).then(setSessions).catch(() => undefined);
    fetchApi('/api/segments').then((r) => (r.ok ? r.json() : { data: [] })).then((d) => setSegments(d.data || [])).catch(() => undefined);
  }, [fetchApi]);

  // Formulários (WhatsApp Flows) das conexões da API oficial escolhidas
  const metaNames = sessions.filter((x) => x.provider === 'META').map((x) => x.name).filter((n) => seq?.connectionNames.includes(n)).join(',');
  useEffect(() => {
    if (!metaNames) { setMetaFlows([]); return; }
    Promise.all(metaNames.split(',').map((n) => fetchApi(`/api/tools/api-oficial/${encodeURIComponent(n)}/flows`).then((r) => (r.ok ? r.json() : { flows: [] })).catch(() => ({ flows: [] }))))
      .then((rs) => setMetaFlows(rs.flatMap((r) => r.flows || [])));
  }, [fetchApi, metaNames]);

  if (!seq) return <div className="py-8"><SkeletonText lines={4} /></div>;

  const patch = (p: Partial<Sequence>) => { setSeq({ ...seq, ...p }); setDirty(true); };
  const patchStep = (i: number, p: Partial<Step>) => { setSteps(steps.map((s, idx) => (idx === i ? { ...s, ...p } : s))); setDirty(true); };
  const move = (i: number, d: -1 | 1) => { const n = [...steps]; [n[i], n[i + d]] = [n[i + d], n[i]]; setSteps(n); setDirty(true); };
  const hasMeta = sessions.some((s) => s.provider === 'META' && seq.connectionNames.includes(s.name));

  const save = async (extra: Record<string, unknown> = {}) => {
    setSaving(true);
    const res = await fetchApi(`/api/sequences/${seq.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        name: seq.name, connectionNames: seq.connectionNames, stopOnReply: seq.stopOnReply, autoCategoryIds: seq.autoCategoryIds,
        steps: steps.map(({ sent: _s, failed: _f, waiting: _w, ...st }) => st), ...extra,
      }),
    });
    const j = await res.json().catch(() => ({}));
    setSaving(false);
    if (!res.ok) { toast.error(j.message || 'Não foi possível salvar'); return false; }
    toast.success('Sequência salva');
    await load();
    return true;
  };

  const upload = async (i: number, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const token = localStorage.getItem('auth_token');
    const r = await fetch('/api/media/upload', { method: 'POST', body: fd, headers: token ? { Authorization: `Bearer ${token}` } : {} });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) { toast.error(data.error || 'Erro no upload'); return; }
    patchStep(i, { content: { ...steps[i].content, url: data.fileUrl, fileName: data.originalName } });
  };

  const enroll = async () => {
    const [kind, value] = enrollSource.split(':');
    const res = await fetchApi(`/api/sequences/${seq.id}/enroll`, { method: 'POST', body: JSON.stringify(kind === 'seg' ? { segmentId: value } : { categoryId: value }) });
    const j = await res.json().catch(() => ({}));
    if (!res.ok) { toast.error(j.message || 'Não foi possível inscrever'); return; }
    toast.success(`${j.enrolled} contato${j.enrolled === 1 ? '' : 's'} inscrito${j.enrolled === 1 ? '' : 's'}${j.alreadyEnrolled ? ` (${j.alreadyEnrolled} já estavam)` : ''}`);
    load(); loadEnrollments();
  };

  const cancel = async (e: Enrollment) => {
    const res = await fetchApi(`/api/sequences/${seq.id}/enrollments/${e.id}/cancel`, { method: 'POST' });
    if (res.ok) { toast.success('Contato removido da sequência'); loadEnrollments(); load(); }
  };

  const remove = async () => {
    const res = await fetchApi(`/api/sequences/${seq.id}`, { method: 'DELETE' });
    if (res.ok) { toast.success('Sequência excluída'); navigate('/sequencias'); }
  };

  const e = seq.enrollments;
  const flowOptions = metaFlows.map((f) => ({ value: f.id, label: `${f.name}${f.status !== 'PUBLISHED' ? ` (${f.status.toLowerCase()})` : ''}` }));
  const enrollOptions = [
    ...segments.map((s) => ({ value: `seg:${s.id}`, label: `Segmento: ${s.name} (${s.contactCount})` })),
    ...categories.map((c) => ({ value: `cat:${c.id}`, label: `Categoria: ${c.nome}` })),
  ];
  const statusOptions = Object.entries(STATUS_LABEL).map(([k, v]) => ({ value: k, label: `${v} (${e[k] ?? 0})` }));

  return (
    <>
      <div style={{ padding: '28px 40px 0', marginBottom: 24 }}>
        <Link to="/sequencias" className="tools-back"><ICONS.ArrowLeft size={16} /> Sequências</Link>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold" style={{ fontSize: 24, fontWeight: 600 }}>{seq.name}</h2>
            {seq.active ? <Badge variant="green">Ativa</Badge> : <Badge variant="neutral">Pausada</Badge>}
          </div>
          {canEdit && (
            <div className="flex items-center gap-2">
              <Button variant="secondary" disabled={!dirty} loading={saving} onClick={() => save()}>Salvar</Button>
              <Button variant="secondary" loading={saving} onClick={() => save({ active: !seq.active })}>
                {seq.active ? 'Pausar' : dirty ? 'Salvar e ativar' : 'Ativar'}
              </Button>
            </div>
          )}
        </div>
        <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>
          {(e.ACTIVE ?? 0)} em andamento · {(e.COMPLETED ?? 0)} concluíram · {(e.CANCELED ?? 0) + (e.FAILED ?? 0)} saíram
        </div>
      </div>

      <div className="page-content space-y-6">
        <section className="brand-card space-y-4">
          <h4 className="text-base font-semibold">Configuração</h4>
          <Field label="Nome">
            <Input onChange={(e) => patch({ name: e.target.value })} value={seq.name} disabled={!canEdit} />
          </Field>
          <div>
            <div className="font-semibold">Conexões que enviam</div>
            <div className="text-xs !text-[var(--text-muted)]" style={{ marginBottom: 6 }}>
              Com mais de uma, os envios se alternam. Cada conexão manda no máximo uma mensagem de sequência a cada 20 segundos.
            </div>
            {sessions.length === 0 && seq.connectionNames.length === 0 && <Banner variant="alert" description={<>Nenhuma conexão conectada.</>} />}
            <div className="flex flex-wrap gap-4">
              {[...new Set([...sessions.map((s) => s.name), ...seq.connectionNames])].map((name) => {
                const s = sessions.find((x) => x.name === name);
                return (
                  <Checkbox onCheckedChange={(checked: boolean) => patch({ connectionNames: checked ? [...seq.connectionNames, name] : seq.connectionNames.filter((n) => n !== name) })} key={name} disabled={!canEdit} checked={seq.connectionNames.includes(name)} label={<>
                    <span className="inline-flex items-center gap-1">{s && <BrandLogo brand={providerBrand(s.provider)} size={14} rounded={3} />}{`${s?.displayName || s?.mePushName || name}${s ? '' : ' (desconectada)'}`}</span>
                  </>} />
                );
              })}
            </div>
          </div>
          <Checkbox onCheckedChange={(checked: boolean) => patch({ stopOnReply: checked })} disabled={!canEdit} checked={seq.stopOnReply} label={<>
            Parar para quem responder (recomendado: a conversa segue com a equipe)
          </>} />
          <div>
            <div className="font-semibold">Inscrição automática</div>
            <div className="text-xs !text-[var(--text-muted)]" style={{ marginBottom: 6 }}>
              Contato que entrar numa destas categorias (cadastro, importação, API ou edição) é inscrito sozinho.
            </div>
            <div className="flex flex-wrap gap-4">
              {categories.map((c) => (
                <Checkbox onCheckedChange={(checked: boolean) => patch({ autoCategoryIds: checked ? [...seq.autoCategoryIds, c.id] : seq.autoCategoryIds.filter((x) => x !== c.id) })} key={c.id} disabled={!canEdit} checked={seq.autoCategoryIds.includes(c.id)} label={<>
                  {c.nome}
                </>} />
              ))}
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <h4 className="text-base font-semibold">Passos</h4>
          {steps.length === 0 && <Banner variant="default" description={<>Nenhum passo ainda. Adicione a primeira mensagem.</>} />}
          {steps.map((s, i) => {
            const typeOptions = (Object.keys(TYPE_LABEL) as MessageType[])
              .filter((t) => !['template', 'flow'].includes(t) || hasMeta || s.messageType === t)
              .map((t) => ({ value: t, label: TYPE_LABEL[t] }));
            return (
            <div key={s.id ?? `new-${i}`} className="brand-card space-y-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Badge variant="blue">Passo {i + 1}</Badge>
                  {s.id && <div className="text-xs !text-[var(--text-muted)]">{s.sent ?? 0} enviadas · {s.failed ?? 0} falhas · {s.waiting ?? 0} aguardando</div>}
                </div>
                {canEdit && (
                  <div className="flex gap-1">
                    <Button variant="ghost" shape="square" icon={ICONS.ArrowUp} aria-label="Subir" disabled={i === 0} onClick={() => move(i, -1)} />
                    <Button variant="ghost" shape="square" icon={ICONS.ArrowDown} aria-label="Descer" disabled={i === steps.length - 1} onClick={() => move(i, 1)} />
                    <Button variant="ghost" shape="square" icon={ICONS.Trash} aria-label="Remover passo" onClick={() => { setSteps(steps.filter((_, idx) => idx !== i)); setDirty(true); }} />
                  </div>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2" style={{ fontSize: 13 }}>
                <span>Enviar</span>
                <input aria-label="Atraso" type="number" min={0} style={{ ...input, width: 80 }} value={s.delayValue} disabled={!canEdit}
                  onChange={(ev) => patchStep(i, { delayValue: Math.max(0, Number(ev.target.value) || 0) })} />
                <select aria-label="Unidade" style={input} value={s.delayUnit} disabled={!canEdit} onChange={(ev) => patchStep(i, { delayUnit: ev.target.value as Step['delayUnit'] })}>
                  {Object.entries(UNIT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <span>{i === 0 ? 'depois de entrar na sequência' : 'depois do passo anterior'}</span>
              </div>

              <div className="flex flex-wrap items-center gap-2" style={{ fontSize: 13 }}>
                <label className="flex items-center gap-2">
                  <Switch onCheckedChange={(checked: boolean) => patchStep(i, checked ? { windowStart: '09:00', windowEnd: '19:00' } : { windowStart: null, windowEnd: null })} aria-label="Só em horário" checked={!!s.windowStart} disabled={!canEdit} />
                  <span>Só entre</span>
                </label>
                <input aria-label="Início" type="time" style={input} value={s.windowStart ?? ''} disabled={!canEdit || !s.windowStart} onChange={(ev) => patchStep(i, { windowStart: ev.target.value })} />
                <span>e</span>
                <input aria-label="Fim" type="time" style={input} value={s.windowEnd ?? ''} disabled={!canEdit || !s.windowStart} onChange={(ev) => patchStep(i, { windowEnd: ev.target.value })} />
                <span>(Brasília), nos dias</span>
                {DAYS.map((d, di) => (
                  <button key={d} type="button" disabled={!canEdit}
                    onClick={() => patchStep(i, { windowDays: s.windowDays.includes(di) ? s.windowDays.filter((x) => x !== di) : [...s.windowDays, di].sort() })}
                    style={{ ...input, padding: '4px 8px', background: s.windowDays.includes(di) ? 'var(--tint-green)' : 'var(--surface)', borderColor: s.windowDays.includes(di) ? 'var(--brand-text)' : 'var(--border-strong)' }}>
                    {d}
                  </button>
                ))}
              </div>

              <Field label="Tipo de mensagem">
                <Select value={(typeOptions.find((o) => o.value === s.messageType) ?? null)?.value ?? null} items={typeOptions} onValueChange={(value: string | null) => {
                    const v = value as MessageType;
                    patchStep(i, {
                      messageType: v,
                      content: v === 'text' ? { text: s.content.text || '' } : v === 'template' ? { templateName: '', language: 'pt_BR', params: [] } : v === 'flow' ? { flowRowId: '', body: s.content.text || '', cta: 'Preencher' } : { caption: s.content.caption || s.content.text || '' },
                    });
                  }} disabled={!canEdit} />
              </Field>

              {s.messageType === 'text' && (
                <>
                  <InputArea label="Mensagem" onChange={(e) => patchStep(i, { content: { ...s.content, text: e.target.value } })} rows={4} value={s.content.text || ''} disabled={!canEdit} />
                  {canEdit && (
                    <div className="flex flex-wrap gap-2">
                      {variables.map((v) => <Button variant="secondary" key={v} onClick={() => patchStep(i, { content: { ...s.content, text: `${s.content.text || ''}${v}` } })}>{v}</Button>)}
                    </div>
                  )}
                </>
              )}

              {['image', 'video', 'audio', 'document'].includes(s.messageType) && (
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    {canEdit && <input aria-label="Arquivo" type="file" onChange={(ev) => ev.target.files?.[0] && upload(i, ev.target.files[0])} />}
                    {s.content.url && <div className="text-xs !text-[var(--brand-text)]">✓ {s.content.fileName || 'arquivo enviado'}</div>}
                  </div>
                  {s.messageType !== 'audio' && (
                    <InputArea label="Legenda (opcional)" onChange={(e) => patchStep(i, { content: { ...s.content, caption: e.target.value } })} rows={2} value={s.content.caption || ''} disabled={!canEdit} />
                  )}
                </div>
              )}

              {s.messageType === 'flow' && (
                <div className="space-y-3">
                  {metaFlows.length === 0 && (
                    <Banner variant="default" description={<>Nenhum formulário sincronizado. Em Ferramentas › API oficial › Formulários, busque os Flows da conta.</>} />
                  )}
                  <Field label="Formulário">
                    <Select value={(flowOptions.find((o) => o.value === (s.content.flowRowId || '')) ?? null)?.value ?? null} items={flowOptions} onValueChange={(value: string | null) => patchStep(i, { content: { ...s.content, flowRowId: value } })} placeholder="Escolha o formulário" disabled={!canEdit} />
                  </Field>
                  <InputArea label="Texto da mensagem" onChange={(e) => patchStep(i, { content: { ...s.content, body: e.target.value } })} rows={3} value={s.content.body || ''} disabled={!canEdit} />
                  <Field label="Texto do botão">
                    <Input onChange={(e) => patchStep(i, { content: { ...s.content, cta: e.target.value.slice(0, 30) } })} value={s.content.cta || ''} disabled={!canEdit} />
                  </Field>
                  <div className="text-xs !text-[var(--text-muted)]">
                    O formulário só sai para quem falou com você nas últimas 24 h (regra da Meta). As respostas vão para os campos ligados em Formulários.
                  </div>
                </div>
              )}

              {s.messageType === 'template' && (
                <div className="grid gap-3 sm:grid-cols-3">
                  <Field label="Nome do template aprovado">
                    <Input onChange={(e) => patchStep(i, { content: { ...s.content, templateName: e.target.value } })} value={s.content.templateName || ''} disabled={!canEdit} />
                  </Field>
                  <Field label="Idioma">
                    <Input onChange={(e) => patchStep(i, { content: { ...s.content, language: e.target.value } })} value={s.content.language || 'pt_BR'} disabled={!canEdit} />
                  </Field>
                  <Field label="Parâmetros (separados por |)" description="Ex.: {{primeiro_nome}}|{{cidade}}">
                    <Input onChange={(e) => patchStep(i, { content: { ...s.content, params: e.target.value.split('|') } })} value={(s.content.params || []).join('|')} disabled={!canEdit} />
                  </Field>
                </div>
              )}
            </div>
            );
          })}
          {canEdit && (
            <Button variant="secondary" icon={ICONS.Plus} onClick={() => { setSteps([...steps, newStep(steps.length === 0)]); setDirty(true); }}>
              Adicionar passo
            </Button>
          )}
          {hasMeta && <Banner variant="default" description={<>Pela API oficial, mensagem fora da janela de 24 h só sai como template aprovado.</>} />}
        </section>

        <section className="brand-card space-y-3">
          <h4 className="text-base font-semibold">Inscrever contatos</h4>
          <div style={{ color: 'var(--text-muted)' }}>
            Quem já está na sequência continua de onde parou. Também dá para inscrever pelo bloco "Sequência" do fluxo interativo e pela API.
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div style={{ minWidth: 280 }}>
              <Field label="De onde">
                <Select value={(enrollOptions.find((o) => o.value === enrollSource) ?? null)?.value ?? null} items={enrollOptions} onValueChange={(value: string | null) => setEnrollSource(value ?? '')} placeholder="Escolha um segmento ou categoria" disabled={!canEdit} />
              </Field>
            </div>
            <Button variant="primary" disabled={!canEdit || !enrollSource} onClick={enroll}>Inscrever</Button>
          </div>
          {!seq.active && <Banner variant="alert" description={<>A sequência está pausada: os inscritos só recebem depois de ativar.</>} />}
        </section>

        <section className="brand-card space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h4 className="text-base font-semibold">Inscritos</h4>
            <div style={{ width: 200 }}>
              <Select value={(statusOptions.find((o) => o.value === enrollFilter) ?? null)?.value ?? null} items={statusOptions} onValueChange={(value: string | null) => setEnrollFilter(value ?? '')} aria-label="Situação" />
            </div>
          </div>
          {enrollments.length === 0 && <div style={{ color: 'var(--text-muted)' }}>Ninguém nesta situação.</div>}
          {enrollments.map((en) => (
            <div key={en.id} className="flex items-center justify-between gap-3" style={{ borderTop: '1px solid var(--border)', paddingTop: 8 }}>
              <div style={{ minWidth: 0 }}>
                <div className="font-semibold">{en.contact.nome} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>{en.contact.telefone}</span></div>
                <div className="text-xs !text-[var(--text-muted)]">
                  {en.status === 'ACTIVE' ? `Próximo: passo ${en.currentStep + 1}${en.nextRunAt ? ` em ${new Date(en.nextRunAt).toLocaleString('pt-BR')}` : ''}` : STATUS_LABEL[en.status]}
                  {en.stopReason ? ` · ${STOP_LABEL[en.stopReason] ?? en.stopReason}` : ''}
                  {en.lastError ? ` · ${en.lastError}` : ''}
                </div>
              </div>
              {canEdit && en.status === 'ACTIVE' && <Button variant="secondary" onClick={() => cancel(en)}>Remover</Button>}
            </div>
          ))}
        </section>

        {canEdit && (
          <div>
            <DangerButton variant="dangerOutline" icon={ICONS.Trash}  onClick={() => setDeleting(true)}>
              Excluir sequência
            </DangerButton>
          </div>
        )}
      </div>

      <DangerConfirmModal open={deleting} title="Excluir sequência" confirmText="Excluir" objectName={seq.name} onCancel={() => setDeleting(false)} onConfirm={remove}>
        A sequência <strong>{seq.name}</strong>, os passos e o histórico dos {Object.values(e).reduce<number>((a, b) => a + (b ?? 0), 0)} inscritos são apagados. Não dá para desfazer.
      </DangerConfirmModal>
    </>
  );
}
